import { ChangeEvent, useState, useRef } from 'react'
import { Label, Textarea, Button } from '@creanizer/ui'
import { Upload, FileText, X } from 'lucide-react'
import type { SocietyInput } from '@creanizer/types'
import { readMultipleFiles, FILE_ACCEPT, formatFileSize } from '../../utils/fileReader'

interface IdeaModeFormProps {
  input: SocietyInput
  onChange: (input: SocietyInput) => void
}

interface UploadedFile {
  id: string
  name: string
  content: string
  size: number
}

export function IdeaModeForm({ input, onChange }: IdeaModeFormProps) {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleChange = (field: keyof SocietyInput, value: unknown) => {
    onChange({ ...input, [field]: value })
  }

  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    try {
      const { fileInfos, errors } = await readMultipleFiles(files)

      if (errors.length > 0) {
        alert(`以下のファイルの読み込みに失敗しました:\n${errors.join('\n')}`)
      }

      const newFiles = fileInfos
        .filter(info => !info.error && info.text)
        .map(info => ({
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          name: info.fileName,
          content: info.text,
          size: info.text.length
        }))

      const allFiles = [...uploadedFiles, ...newFiles]
      setUploadedFiles(allFiles)

      const allFilesContent = allFiles
        .map(file => `【ファイル: ${file.name}】\n${file.content}`)
        .join('\n\n')
      onChange({ ...input, uploadedContent: allFilesContent })
    } catch (error) {
      console.error('ファイル読み込みエラー:', error)
      alert('ファイルの読み込みに失敗しました')
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleFileRemove = (fileId: string) => {
    const newFiles = uploadedFiles.filter(f => f.id !== fileId)
    setUploadedFiles(newFiles)

    const allFilesContent = newFiles
      .map(file => `【ファイル: ${file.name}】\n${file.content}`)
      .join('\n\n')
    onChange({ ...input, uploadedContent: allFilesContent || undefined })
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="ideaText">アイディア</Label>
        <Textarea
          id="ideaText"
          value={input.ideaText || ''}
          onChange={(e) => handleChange('ideaText', e.target.value)}
          placeholder="組織のアイディアを自由に入力してください。
例：
- 「表向きは慈善団体だが、裏では情報収集を行うスパイ組織」
- 「古代の遺物を収集し、その力で世界征服を企む秘密結社」
- 「異世界からの侵略者と戦う秘密の防衛組織」
- 「魔法と科学を融合させた新しい学問を追求する研究機関」"
          rows={8}
        />
      </div>

      {/* ファイルアップロード */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>参考ファイルアップロード</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-4 w-4 mr-2" />
            ファイルを選択
          </Button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={FILE_ACCEPT}
          multiple
          onChange={handleFileUpload}
          className="hidden"
        />

        {/* 読み込んだファイルのリスト */}
        {uploadedFiles.length > 0 && (
          <div className="space-y-2">
            <div className="p-3 bg-gray-50 rounded-lg border">
              <div className="text-sm font-medium text-gray-700 mb-2">読み込み済みファイル：</div>
              <div className="space-y-2">
                {uploadedFiles.map((file) => (
                  <div key={file.id} className="flex items-center justify-between bg-white p-2 rounded border">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-gray-500" />
                      <span className="text-sm">{file.name}</span>
                      <span className="text-xs text-gray-500">({formatFileSize(file.size)})</span>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => handleFileRemove(file.id)}
                      className="p-1"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-500">
              <FileText className="h-4 w-4" />
              <span>
                合計 {uploadedFiles.length} ファイル（{formatFileSize(uploadedFiles.reduce((sum, f) => sum + f.size, 0))}）
              </span>
            </div>
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          複数のファイルを同時にアップロード可能です（対応形式: .txt, .md, .docx, .pdf）
        </p>
      </div>

      <div className="p-4 bg-muted rounded-lg">
        <h4 className="font-semibold mb-2">アイディアモードのヒント</h4>
        <ul className="text-sm text-muted-foreground space-y-1">
          <li>・組織の表と裏の顔を描写すると面白い設定になります</li>
          <li>・具体的な目的や野望を含めると、より詳細な設定が生成されます</li>
          <li>・他の組織との関係性や対立構造を含めることもできます</li>
          <li>・特殊な能力や技術、独自の文化などを追加できます</li>
        </ul>
      </div>
    </div>
  )
}
